/* eslint-disable @typescript-eslint/ban-ts-comment */
import { MapProjection } from "@/types/enum";
import { MAPBOX_ACCESS_TOKEN } from "@/constant";
import useMapboxStore from "@/stores/useMapboxStore";
import { autobind } from "core-decorators";
import { Map, type LayerSpecification } from "mapbox-gl";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import proj4 from "proj4";
// import { ArcLayer, GeoJsonLayer, ScatterplotLayer } from "@deck.gl/layers";

// import * as turf from "@turf/turf";

import SimShadyShadowLayer from "@/components/mapbox/Lib/Shading/SimShadyShadowLayer";

import PolygonEntity from "@/components/mapbox/Lib/Entities/PolygonEntity";
import MeasureLengthEntity from "@/components/mapbox/Lib/Entities/MeasureLengthEntity";
import MeasureAreaEntity from "@/components/mapbox/Lib/Entities/MeasureAreaEntity";
import TileOverlayEntity from "@/components/mapbox/Lib/Entities/TileOverlayEntity";
import EditorPolygonController from "@/components/mapbox/Lib/Editors/EditorPolygonController";
import EditorMeasureLengthController from "@/components/mapbox/Lib/Editors/EditorMeasureLengthController";
import EditorMeasureAreaController from "@/components/mapbox/Lib/Editors/EditorMeasureAreaController";

// Import drawing controllers
import DrawingPolygonController from "@/components/mapbox/Lib/Controllers/DrawingPolygonController";
import DrawingOffsetController from "@/components/mapbox/Lib/Controllers/DrawingOffsetController";
import MeasureLengthController from "@/components/mapbox/Lib/Controllers/MeasureLengthController";
import MeasureAreaController from "@/components/mapbox/Lib/Controllers/MeasureAreaController";
// import EditController from "@/components/mapbox/Lib/Controllers/EditController";
import InteractionController from "@/components/mapbox/Lib/Controllers/InteractionController";
import BuildingEntity from "@/components/mapbox/Lib/Entities/BuildingEntity";
import EditorBuildingController from "@/components/mapbox/Lib/Editors/EditorBuildingController";
import useAppStore from "@/stores/useAppStore";

mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

interface MapboxEntities {
  polygonEntity: PolygonEntity | null;
  buildingEntity: BuildingEntity | null;
  measureLengthEntity: MeasureLengthEntity | null;
  measureAreaEntity: MeasureAreaEntity | null;
  tileOverlayEntity: TileOverlayEntity | null;
}
interface MapboxEditors {
  editorPolygonController: EditorPolygonController | null;
  editorBuildingController: EditorBuildingController | null;
  editorMeasureLengthController: EditorMeasureLengthController | null;
  editorMeasureAreaController: EditorMeasureAreaController | null;
}

interface MapboxControllers {
  drawingPolygonController: DrawingPolygonController | null;
  measureLengthController: MeasureLengthController | null;
  measureAreaController: MeasureAreaController | null;
  interactionController: InteractionController | null;
  drawingOffsetController: DrawingOffsetController | null;
}

interface LocationSaved {
  projectId: string;
  center: number[];
  zoom: number;
  style: string;
}
class MapboxViewer {
  //@ts-ignore
  public _mapViewer: Map;
  public projectId: string;
  //@ts-ignore
  public controllers: MapboxControllers;
  //@ts-ignore
  public entities: MapboxEntities = {
    polygonEntity: null,
    buildingEntity: null,
    measureLengthEntity: null,
    measureAreaEntity: null,
    tileOverlayEntity: null,
  };
  //@ts-ignore
  public editors: MapboxEditors;
  public shadeMap: SimShadyShadowLayer | null = null;

  private _3dMapAvailable = {
    base_map: false,
  };
  private element: HTMLElement;
  constructor(element: HTMLElement, projectId: string) {
    this.element = element;
    this.projectId = projectId;
  }
  @autobind
  async init() {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      const locationsSaved = localStorage.getItem("mapbox-locations");
      let locationData = {
        center: [7.579018135230854, 51.96693841988372],
        zoom: 18,
        style: "mapbox://styles/mapbox/streets-v12",
      };
      if (locationsSaved) {
        const projectLocations = JSON.parse(locationsSaved) as LocationSaved[];
        const projectLocation = projectLocations.find(
          (location: LocationSaved) => location.projectId === this.projectId,
        );
        const lng = projectLocation?.center[0] || 7.579018135230854;
        const lat = projectLocation?.center[1] || 51.96693841988372;
        const zoom = projectLocation?.zoom || 18;
        const style =
          projectLocation?.style || "mapbox://styles/mapbox/streets-v12";
        locationData = {
          center: [lng, lat],
          zoom: zoom,
          style: style,
        };
      }
      this._mapViewer = new Map({
        container: this.element,
        style: "mapbox://styles/mapbox/streets-v12",
        // center: [-122.51473530281777, 37.70463582140094],
        // center: [7.0982, 51.4816],
        center: [locationData.center[0], locationData.center[1]],
        zoom: locationData.zoom,
        maxZoom: 24,
        minZoom: 10,
        // projection: "mercator",
      });
      // @ts-ignore
      window.map = this._mapViewer;
      // useMapboxStore.getState().setMapStyle(locationData.style);

      // Enable XY-plane rotation from the start (2D mode)
      this._mapViewer.dragRotate.enable(); // Allow horizontal rotation (bearing changes)
      this._mapViewer.touchPitch.disable(); // Disable pitch changes to keep it at 0°
      this._mapViewer.keyboard.disable(); // Disable keyboard pitch changes

      // Lock pitch at 0° for true 2D mode
      this._mapViewer.setMaxPitch(0);
      this._mapViewer.setMinPitch(0);

      await this.onLoad();
      // Initialize drawing controllers
      this.initializeDrawingControllers();
      this.loadDataset();

      // Save center to localStorage when the map center changes
      this._mapViewer.on("moveend", () => {
        const center = this._mapViewer.getCenter();
        const zoom = this._mapViewer.getZoom();
        const style =
          this._mapViewer.getStyle()?.sprite ||
          "mapbox://styles/mapbox/streets-v11";
        const locationsSaved = localStorage.getItem("mapbox-locations");
        if (locationsSaved) {
          const projectLocations = JSON.parse(
            locationsSaved,
          ) as LocationSaved[];
          const currentLocation = projectLocations.find(
            (location: LocationSaved) => location.projectId === this.projectId,
          );
          if (currentLocation) {
            currentLocation.center = [center.lng, center.lat];
            currentLocation.zoom = zoom;
          } else {
            projectLocations.push({
              projectId: this.projectId,
              center: [center.lng, center.lat],
              zoom: zoom,
              style: style,
            });
          }
          localStorage.setItem(
            "mapbox-locations",
            JSON.stringify(projectLocations),
          );
        } else {
          localStorage.setItem(
            "mapbox-locations",
            JSON.stringify([
              {
                projectId: this.projectId,
                center: [center.lng, center.lat],
                zoom: zoom,
              },
            ]),
          );
        }
      });

      resolve(true);
    });
  }

  // private loadShadowLayer() {
  //   // Sun position
  //   const now = new Date();
  //   const lat = 40.7128,
  //     lng = -74.006;
  //   const sunPos = SunCalc.getPosition(now, lat, lng);

  //   const altitude = sunPos.altitude; // radians
  //   const azimuth = sunPos.azimuth + Math.PI; // radians, corrected

  //   // Shadow vector (dx, dy per height unit)
  //   const shadowLengthFactor = 1 / Math.tan(altitude);
  //   const dx = Math.sin(azimuth) * shadowLengthFactor;
  //   const dy = Math.cos(azimuth) * shadowLengthFactor;

  //   // Get building footprints from Mapbox tiles
  //   this._mapViewer.addSource("building-shadows", {
  //     type: "geojson",
  //     data: {
  //       type: "FeatureCollection",
  //       features: [],
  //     },
  //   });

  //   this._mapViewer.addLayer({
  //     id: "shadows",
  //     type: "fill",
  //     source: "building-shadows",
  //     paint: {
  //       "fill-color": "rgba(50,50,50,0.5)",
  //       "fill-opacity": 0.5,
  //     },
  //   });

  //   // Query buildings in view
  //   this._mapViewer.once("idle", async () => {
  //     const buildings = this._mapViewer.querySourceFeatures("composite", {
  //       sourceLayer: "building",
  //     });

  //     const shadowFeatures = buildings.map((b: any) => {
  //       const height = b.properties.height || 20;
  //       const geom = turf.getCoords(b); // needs Turf.js
  //       const shadowOffset = [dx * height * 0.00001, dy * height * 0.00001];

  //       // Shift footprint by shadow vector
  //       const shadowGeom = turf.transformTranslate(
  //         turf.polygon(geom),
  //         Math.sqrt(shadowOffset[0] ** 2 + shadowOffset[1] ** 2),
  //         (azimuth * 180) / Math.PI,
  //         { units: "degrees" }
  //       );

  //       return {
  //         type: "Feature",
  //         geometry: shadowGeom.geometry,
  //       };
  //     });

  //     this._mapViewer.getSource("building-shadows")?.setData({
  //       type: "FeatureCollection",
  //       features: shadowFeatures,
  //     });
  //   });
  // }

  // @autobind
  // private mapLoaded(map: Map) {
  //   return new Promise((res) => {
  //     function cb() {
  //       if (!map.loaded()) {
  //         return;
  //       }
  //       map.off("render", cb);
  //       res(true);
  //     }
  //     map.on("render", cb);
  //     cb();
  //   });
  // }
  @autobind
  public setShadingOverlayActive(active: boolean) {
    this.entities.polygonEntity?.setShadingOverlayActive(active);
    this.entities.buildingEntity?.setShadingOverlayActive(active);
  }

  @autobind
  public loadShadowLayer() {
    const localMapViewer = this._mapViewer;
    this.shadeMap = new SimShadyShadowLayer({
      date: new Date(),
      onProgress: (progress) => {
        useMapboxStore.getState().setShadowMeshProgress(progress);
      },
      setShadingOverlayActive: (active) => this.setShadingOverlayActive(active),
      getFeatures: async () => {
        await this.mapLoaded(localMapViewer);
        const buildingData = localMapViewer
          .querySourceFeatures("composite", { sourceLayer: "building" })
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .filter((feature: any) => {
            return (
              feature.properties &&
              feature.properties.underground !== "true" &&
              (feature.properties.height || feature.properties.render_height)
            );
          });

        const polygonData = localMapViewer
          .querySourceFeatures("polygon-source", {})
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .filter((feature: any) => {
            return (
              feature.properties &&
              feature.geometry.type === "Polygon" &&
              feature.properties.height > 0
            );
          });

        const modelData = localMapViewer
          .querySourceFeatures("building-source", {
            sourceLayer: "building-polygons",
          })
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .filter((feature: any) => {
            return (
              feature.geometry &&
              feature.geometry.type === "Polygon" &&
              feature.properties.height > 0
            );
          });

        return [...buildingData, ...polygonData, ...modelData];
      },
      debug: (msg) => {
        console.log(new Date().toISOString(), msg);
      },
    }).addTo(localMapViewer);
  }
  @autobind
  private mapLoaded(map: Map) {
    return new Promise((res) => {
      function cb() {
        if (!map.loaded()) {
          return;
        }
        map.off("render", cb);
        res(true);
      }
      map.on("render", cb);
      cb();
    });
  }
  // @autobind
  // private loadShadowLayer() {
  //   const now = new Date(
  //     SunCalc.getTimes(
  //       new Date(),
  //       47.694878957368815,
  //       -122.18578164139899
  //     ).sunrise.getTime() +
  //       60 * 60 * 1000
  //   );
  //   const localMapViewer = this._mapViewer;
  //   this.shadeMap = new ShadeMap({
  //     date: now, // display shadows for current date
  //     color: "#01112f", // shade color
  //     opacity: 0.7, // opacity of shade color
  //     apiKey: SHAD_MAP_API_KEY, // obtain from https://shademap.app/about/
  //     terrainSource: {
  //       maxZoom: 15,
  //       tileSize: 256,
  //       getSourceUrl: ({ x, y, z }) =>
  //         `https://s3.amazonaws.com/elevation-tiles-prod/terrarium/${z}/${x}/${y}.png`,
  //       getElevation: ({ r, g, b }) => r * 256 + g + b / 256 - 32768,
  //       _overzoom: 18,
  //     },
  //     getFeatures: async () => {
  //       await this.mapLoaded(localMapViewer);
  //       const buildingData = localMapViewer
  //         .querySourceFeatures("composite", { sourceLayer: "building" })
  //         .filter((feature: Feature) => {
  //           return (
  //             feature.properties &&
  //             feature.properties.underground !== "true" &&
  //             (feature.properties.height || feature.properties.render_height)
  //           );
  //         });
  //       return buildingData;
  //     },
  //     // sunExposure: {
  //     //   enabled: true,
  //     //   startDate: new Date(Date.now() + 1000 * 60 * 60 * 12),
  //     //   endDate: new Date(Date.now() + 1000 * 60 * 60 * 13),
  //     // },
  //     debug: (msg) => {
  //       console.log(new Date().toISOString(), msg);
  //     },
  //   }).addTo(localMapViewer);
  //   this.shadeMap.on("tileloaded", (loadedTiles, totalTiles) => {
  //     console.log(`Loading: ${((loadedTiles / totalTiles) * 100).toFixed(0)}%`);
  //   });
  //   // shadeMap.setDate(new Date(Date.now() + 1000 * 60 * 60 * 12));
  // }
  @autobind
  initializeDrawingControllers() {
    this.controllers = {
      drawingPolygonController: new DrawingPolygonController(this),
      measureLengthController: new MeasureLengthController(this),
      measureAreaController: new MeasureAreaController(this),
      interactionController: new InteractionController(this),
      drawingOffsetController: new DrawingOffsetController(this),
    };
    this.controllers.interactionController!.enabled = true;
    this.entities = {
      polygonEntity: new PolygonEntity(this),
      buildingEntity: new BuildingEntity(this),
      measureLengthEntity: new MeasureLengthEntity(this),
      measureAreaEntity: new MeasureAreaEntity(this),
      tileOverlayEntity: new TileOverlayEntity(this),
    };
    this.editors = {
      editorPolygonController: new EditorPolygonController(this),
      editorBuildingController: new EditorBuildingController(this),
      editorMeasureLengthController: new EditorMeasureLengthController(this),
      editorMeasureAreaController: new EditorMeasureAreaController(this),
    };
  }

  disposeControllers() {
    for (const controller of Object.values(this.controllers)) {
      controller!.dispose();
    }
    for (const editor of Object.values(this.editors)) {
      editor!.dispose();
    }
  }

  @autobind
  disableAllDrawingTools() {
    for (const controller of Object.values(this.controllers)) {
      controller!.enabled = false;
    }
  }

  @autobind
  enableDrawingTool(toolName: string) {
    this.disableAllDrawingTools();
    switch (toolName) {
      case "polygon":
        this.controllers.drawingPolygonController!.enabled = true;
        break;
      case "draw_offset":
        this.controllers.drawingOffsetController!.enabled = true;
        break;
      case "measure_length":
        this.controllers.measureLengthController!.enabled = true;
        break;
      case "measure_area":
        this.controllers.measureAreaController!.enabled = true;
        break;
      default:
        this.controllers.interactionController!.enabled = true;
        break;
    }
  }
  @autobind
  addWmsLayer(layerName: string) {
    const checkLayer = this._mapViewer.getLayer(layerName);
    if (checkLayer) {
      return;
    }
    // <<< NEW ─ helper to add WMS source + layer
    const wmsTileURL =
      `https://www.wms.nrw.de/geobasis/wms_nw_alkis?service=WMS&request=GetMap&version=1.1.1` +
      `&layers=${layerName}&styles=&format=image/png&transparent=true` +
      `&height=256&width=256&srs=EPSG:3857` + // Web-Mercator
      `&bbox={bbox-epsg-3857}`; // Mapbox fills this

    this._mapViewer.addSource("custom-" + layerName + "-source", {
      type: "raster",
      tiles: [wmsTileURL],
      tileSize: 256,
    });

    this._mapViewer.addLayer({
      id: "custom-" + layerName,
      type: "raster",
      source: "custom-" + layerName + "-source",
      paint: { "raster-opacity": 0.7 }, // semi-transparent
    });
  }
  @autobind
  removeWmsLayer(layerName: string) {
    if (!this._mapViewer.getLayer("custom-" + layerName)) {
      return;
    }
    this._mapViewer.removeLayer("custom-" + layerName);
    this._mapViewer.removeSource("custom-" + layerName + "-source");
  }

  @autobind
  addBaseMapLayer() {
    if (!this._mapViewer.getLayer("lod2_building")) {
      this._3dMapAvailable["base_map"] = true;
      // @ts-ignore
      const lod2_layer = new Mapbox3DTiles.Mapbox3DTilesLayer({
        id: "lod2_building",
        url: "https://sg.geodatenzentrum.de/gdz_basemapde_3d_gebaeude/lod2_3857_null.json",
        colorWall: "#c2c2c2",
        colorRoof: "#6b7280",
        colorBridge: "#999999",
        map: this._mapViewer,
      });

      this._mapViewer.addLayer(lod2_layer);
    }
  }
  @autobind
  removeBaseMapLayer() {
    this._3dMapAvailable["base_map"] = false;
    if (this._mapViewer.getLayer("lod2_building")) {
      this._mapViewer.removeLayer("lod2_building");
    }
  }
  @autobind
  loadDataset() {
    if (this._3dMapAvailable["base_map"]) {
      this.addBaseMapLayer();
    } else {
      this.removeBaseMapLayer();
    }

    // this._mapViewer.addSource("Timezones", {
    //   type: "vector",
    //   tiles: ["https://cfw.shademap.app/timezones-TZID/{z}/{x}/{y}.mvt"],
    //   minzoom: 0,
    //   maxzoom: 9,
    // });
    // this._mapViewer.addLayer({
    //   id: "Timezones",
    //   source: "Timezones",
    //   "source-layer": "Timezones",
    //   type: "fill",
    //   paint: {
    //     "fill-color": "red",
    //     "fill-opacity": 0,
    //   },
    // });

    // this._mapViewer.setLights([
    //   {
    //     id: "ambient",
    //     type: "ambient",
    //     properties: {
    //       intensity: 0.5,
    //     },
    //   },
    //   {
    //     id: "directional",
    //     type: "directional",
    //     properties: {
    //       intensity: 1,
    //       direction: [100, 70],
    //       "cast-shadows": true,
    //       "shadow-intensity": 1,
    //     },
    //   },
    // ]);
  }
  @autobind
  async changeStyle(newStyleUrl: string) {
    const customLayers: LayerSpecification[] = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const customSources: { id: string; source: any }[] = [];

    // Before changing style, save current layers
    this._mapViewer!.getStyle()!.layers.forEach((layer) => {
      if (layer.id.startsWith("custom-")) {
        // or your naming convention
        customLayers.push(layer);
      }
    });

    // Save sources too
    Object.keys(this._mapViewer!.getStyle()!.sources).forEach((sourceId) => {
      if (sourceId.startsWith("custom-")) {
        customSources.push({
          id: sourceId,
          source: this._mapViewer.getSource(sourceId),
        });
      }
    });

    this._mapViewer.on("style.load", () => {
      useAppStore.getState().setIsAppLoading(true);
      const projection = useMapboxStore.getState().mapProjection;
      if (projection === MapProjection.Globe) {
        this._mapViewer.setCamera({ "camera-projection": "perspective" });
      } else {
        this._mapViewer.setCamera({ "camera-projection": "orthographic" });
      }

      // Re-add sources first
      customSources.forEach(({ id, source }) => {
        if (!this._mapViewer.getSource(id)) {
          this._mapViewer.addSource(id, source);
        }
      });

      // Then re-add layers
      customLayers.forEach((layer) => {
        if (!this._mapViewer.getLayer(layer.id)) {
          this._mapViewer.addLayer(layer);
        }
      });
      this.initializeDrawingControllers();
      this.loadDataset();

      setTimeout(() => {
        useMapboxStore.getState().setMapStyle(newStyleUrl);
        useAppStore.getState().setIsAppLoading(false);
      }, 1500);
    });
    if (this.shadeMap) {
      this.shadeMap.remove();
      this.shadeMap = null;
    }
    useMapboxStore.getState().setIsShadowMode(false);
    this._mapViewer.setStyle(newStyleUrl);
  }
  @autobind
  onLoad() {
    return new Promise((resolve) => {
      const cb = () => {
        if (!this._mapViewer.loaded()) {
          return;
        }
        this._mapViewer.setCamera({ "camera-projection": "orthographic" });
        this._mapViewer.off("load", cb);
        resolve(true);
      };
      this._mapViewer.on("load", cb);
    });
  }
  @autobind
  toggle3D() {
    const projection = useMapboxStore.getState().mapProjection;
    if (projection === MapProjection.Mercator) {
      // Enable 3D mode with pitch for extrusion visibility
      this._mapViewer.setProjection(MapProjection.Globe);

      // Lock pitch at 45° for consistent 3D viewing angle
      this._mapViewer.setMaxPitch(85);
      this._mapViewer.setMinPitch(0);

      // Enable rotation only in XY plane (bearing changes only)
      this._mapViewer.dragRotate.enable();
      this._mapViewer.dragPan.enable(); // Ensure panning is still enabled
      this._mapViewer.touchPitch.disable(); // Disable touch pitch changes
      this._mapViewer.keyboard.disable(); // Disable keyboard pitch changes

      this._mapViewer.setPitch(45); // Set pitch to 45 degrees to see 3D extrusions
      useMapboxStore.getState().setMapProjection(MapProjection.Globe);
      this._mapViewer.setCamera({ "camera-projection": "perspective" });
    } else {
      // Switch back to 2D mode with XY-plane rotation enabled
      this._mapViewer.setProjection(MapProjection.Mercator);

      // Lock pitch at 0° for true 2D mode
      this._mapViewer.setMaxPitch(0);
      this._mapViewer.setMinPitch(0);

      // Enable XY-plane rotation in 2D mode
      this._mapViewer.dragRotate.enable(); // Allow horizontal rotation (bearing changes)
      this._mapViewer.dragPan.enable(); // Ensure panning is enabled
      this._mapViewer.touchPitch.disable(); // Disable pitch changes to keep it at 0°
      this._mapViewer.keyboard.disable(); // Disable keyboard pitch changes

      this._mapViewer.setPitch(0); // Set pitch to 0 to view from directly above
      // Don't reset bearing - let user keep their current rotation
      useMapboxStore.getState().setMapProjection(MapProjection.Mercator);
      this._mapViewer.setCamera({ "camera-projection": "orthographic" });
    }
  }
  @autobind
  zoomIn() {
    this._mapViewer.zoomIn();
  }
  @autobind
  zoomOut() {
    this._mapViewer.zoomOut();
  }
  @autobind
  moveTo(coordinate: number[]) {
    const coordinates = proj4("EPSG:3857", "EPSG:4326", [
      coordinate[0],
      coordinate[1],
    ]);
    this._mapViewer.setCenter([coordinates[0], coordinates[1]]);
    this._mapViewer.setZoom(coordinate[2]);
  }
  @autobind
  getCurrentPosition(): number[] | undefined {
    const zoom = this._mapViewer.getZoom();
    const coordinate = this._mapViewer.getCenter();
    if (coordinate && zoom) {
      return [coordinate.lng, coordinate.lat, zoom];
    }
    return undefined;
  }
  @autobind
  toggleCamera(toggle: boolean) {
    if (!toggle) {
      this._mapViewer.scrollZoom.disable();
      this._mapViewer.dragPan.disable();
      this._mapViewer.dragRotate.disable();
      this._mapViewer.touchZoomRotate.disableRotation();
    } else {
      this._mapViewer.scrollZoom.enable();
      this._mapViewer.dragPan.enable();
      this._mapViewer.dragRotate.enable();
      this._mapViewer.touchZoomRotate.enableRotation();
    }
  }
  @autobind
  dispose() {
    for (const controller of Object.values(this.controllers)) {
      controller!.dispose();
    }
    // if (this.shadeMap) {
    //   this.shadeMap.remove();
    // }

    // Get all layers and sources from the map style
    const style = this._mapViewer.getStyle();
    if (style) {
      const layers = style.layers || [];
      const sources = style.sources || {};

      // Remove all layers first
      layers.forEach((layer) => {
        if (this._mapViewer.getLayer(layer.id)) {
          this._mapViewer.removeLayer(layer.id);
        }
      });

      // Then remove all sources
      Object.keys(sources).forEach((sourceId) => {
        if (this._mapViewer.getSource(sourceId)) {
          this._mapViewer.removeSource(sourceId);
        }
      });
    }

    // Finally remove the map instance
    this._mapViewer.remove();
  }

  @autobind
  getTZIdAtPoint(): string | null {
    // const map = this._mapViewer;
    // const canvas = this._mapViewer.getCanvas();
    // const rect = canvas?.getBoundingClientRect();
    // const features = map.queryRenderedFeatures(
    //   [
    //     [0, rect?.height],
    //     [rect?.width, 0],
    //   ],
    //   {
    //     layers: ["Timezones"],
    //   }
    // );
    // if (features.length > 0) {
    //   const tzId = features[0].properties?.TZID;
    //   if (tzId) {
    //     return tzId;
    //   }
    // }
    return "Europe/Paris";
  }
}

export default MapboxViewer;
